/**
 * Gestionnaire centralisé pour les clés API IA
 * Fournit une fonction commune pour sauvegarder et tester les clés API des services IA
 */

import { getCircuitBreaker, HEALTHCHECK_MODE } from './circuit-breaker.js';
import { getRouteConfigByRoute } from '../config/routes-config.js';
import debug from '@commons-shared/debug.js';
import { config } from '../config/environment.js';

/**
 * Fonction commune pour sauvegarder et tester une clé API IA
 * @param {Object} params - Paramètres de la fonction
 * @param {string} params.route - La route IA (ex: '/api/ia/text')
 * @param {string} params.apiKeyValue - La valeur de la clé API à sauvegarder
 * @param {Function} params.setEncryptedApiKey - Fonction pour sauvegarder la clé encryptée
 * @param {Function} params.showSuccess - Fonction pour afficher un message de succès
 * @param {Function} params.showError - Fonction pour afficher un message d'erreur
 * @param {Function} params.t - Fonction de traduction
 * @param {string} [params.customDisplayName] - Nom d'affichage personnalisé (ex: nom du modèle sélectionné)
 * @returns {Promise<void>}
 */
export async function saveAndTestApiKey({
  route,
  apiKeyValue,
  setEncryptedApiKey,
  showSuccess,
  showError,
  t,
  customDisplayName
}) {
  // Obtenir la configuration de la route
  const routeConfig = getRouteConfigByRoute(route);
  if (!routeConfig) {
    showError(t('CONFIG', 'ROUTE_CONFIG_NOT_FOUND', `Configuration not found for route: ${route}`));
    return;
  }

  // Utiliser le nom personnalisé ou le nom par défaut de la configuration
  const displayName = customDisplayName || routeConfig.displayName;

  // Vérifier que la clé API est fournie
  if (!apiKeyValue) {
    showError(t('CONFIG', 'API_KEY_REQUIRED', 'API key is required for {{displayName}}', { displayName }));
    return;
  }

  try {
    // Sauvegarder la clé API de manière encryptée
    await setEncryptedApiKey(routeConfig.keyName, apiKeyValue);
    debug.log(config, `Clé API ${displayName} sauvegardée de manière encryptée`);

    // Afficher un message temporaire pendant le test
    showSuccess(t('CONFIG', 'API_KEY_TESTING', '{{displayName}} API key saved, testing in progress...', { displayName }));

    // Obtenir le circuit breaker et lancer le test
    const circuitBreaker = getCircuitBreaker(route);
    if (circuitBreaker) {
      // Ajouter un listener temporaire pour écouter le résultat du test
      const removeListener = circuitBreaker.addListener((status) => {
        if (status.route === route) {
          if (status.isHealthy) {
            showSuccess(t('CONFIG', 'API_KEY_TESTED_SUCCESS', '{{displayName}} API key saved and tested successfully', { displayName }));
          } else {
            showError(t('CONFIG', 'API_KEY_TESTED_ERROR', '{{displayName}} API key saved but test failed: {{errorMessage}}', { 
              displayName,
              errorMessage: status.errorMessage 
            }));
          }
          // Retirer le listener après usage
          removeListener();
        }
      });
      
      // Lancer la vérification de santé en mode utilisateur
      circuitBreaker.checkHealth(HEALTHCHECK_MODE.USER);
    } else {
      showError(t('CONFIG', 'CIRCUIT_BREAKER_NOT_AVAILABLE', 'Circuit breaker not available for testing'));
    }
  } catch (error) {
    debug.error(config, `Error saving ${displayName} API key:`, error);
    showError(t('CONFIG', 'API_KEY_SAVE_ERROR', 'Error saving {{displayName}} API key', { displayName }));
  }
}

/**
 * Créer une fonction de sauvegarde et test spécialisée pour une route IA
 * @param {string} route - La route IA (ex: '/api/ia/text')
 * @param {Function} setEncryptedApiKey - Fonction pour sauvegarder la clé encryptée
 * @param {Function} showSuccess - Fonction pour afficher un message de succès
 * @param {Function} showError - Fonction pour afficher un message d'erreur
 * @param {Function} t - Fonction de traduction
 * @param {string} [customDisplayName] - Nom d'affichage personnalisé (ex: nom du modèle sélectionné)
 * @returns {Function} Fonction spécialisée qui prend seulement l'apiKeyValue
 */
export function createApiKeyHandler(route, setEncryptedApiKey, showSuccess, showError, t, customDisplayName) {
  return async (apiKeyValue) => {
    await saveAndTestApiKey({
      route,
      apiKeyValue,
      setEncryptedApiKey,
      showSuccess,
      showError,
      t,
      customDisplayName
    });
  };
}
